/**
 * Helpers that make the app resilient to React DOM errors caused by code outside React
 * (browser translate, extensions) mutating nodes React owns.
 * Typical symptom: `NotFoundError: Failed to execute 'insertBefore' on 'Node'`.
 */

const DOM_ERROR_RECOVERY_STORAGE_KEY = "worky_dom_error_recovery_state";
const DOM_ERROR_RECOVERY_WINDOW_MS = 60_000;

export enum DomErrorRecoveryAction {
  RESET_BOUNDARY = "RESET_BOUNDARY",
  RELOAD_PAGE = "RELOAD_PAGE",
  NONE = "NONE",
}

interface DomErrorRecoveryState {
  readonly attemptCount: number;
  readonly lastAttemptEpochMs: number;
}

const DOM_ERROR_MESSAGE_PATTERN = /insertBefore|removeChild|appendChild|not a child of this node/i;

export const isDomMutationError = (error: Error | undefined | null): boolean => {
  if (!error) return false;
  return error.name === "NotFoundError" || DOM_ERROR_MESSAGE_PATTERN.test(error.message || "");
};

const readRecoveryState = (): DomErrorRecoveryState => {
  try {
    const storedState: string | null = sessionStorage.getItem(DOM_ERROR_RECOVERY_STORAGE_KEY);
    if (!storedState) return { attemptCount: 0, lastAttemptEpochMs: 0 };
    const parsedState: DomErrorRecoveryState = JSON.parse(storedState);
    const isStale: boolean = Date.now() - parsedState.lastAttemptEpochMs > DOM_ERROR_RECOVERY_WINDOW_MS;
    return isStale ? { attemptCount: 0, lastAttemptEpochMs: 0 } : parsedState;
  } catch (e) {
    return { attemptCount: 0, lastAttemptEpochMs: 0 };
  }
};

const writeRecoveryState = (state: DomErrorRecoveryState): void => {
  try {
    sessionStorage.setItem(DOM_ERROR_RECOVERY_STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    // Storage may be unavailable; recovery then stops after the in-memory attempt.
  }
};

/**
 * Picks and records the next recovery step for a DOM mutation error.
 * Attempt 1 resets the error boundary, attempt 2 reloads the page, then it gives up
 * (within a 60s window) so it can never loop.
 */
export const claimNextDomErrorRecoveryAction = (): DomErrorRecoveryAction => {
  const currentState: DomErrorRecoveryState = readRecoveryState();
  const nextAttemptCount: number = currentState.attemptCount + 1;

  if (nextAttemptCount > 2) return DomErrorRecoveryAction.NONE;

  writeRecoveryState({ attemptCount: nextAttemptCount, lastAttemptEpochMs: Date.now() });
  return nextAttemptCount === 1 ? DomErrorRecoveryAction.RESET_BOUNDARY : DomErrorRecoveryAction.RELOAD_PAGE;
};

let isDomMutationGuardInstalled = false;

/**
 * Well-known React workaround: when outside code moved or removed a node React still references,
 * skip the DOM call instead of throwing and crashing the whole tree.
 * See https://github.com/facebook/react/issues/11538
 */
export const installDomMutationGuard = (): void => {
  if (isDomMutationGuardInstalled || typeof Node !== "function" || !Node.prototype) return;
  isDomMutationGuardInstalled = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, childNode: T): T {
    if (childNode.parentNode !== this) {
      console.warn("DOM guard: removeChild skipped, node is not a child of this parent", childNode);
      return childNode;
    }
    return originalRemoveChild.call(this, childNode) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn("DOM guard: insertBefore skipped, reference node is not a child of this parent", referenceNode);
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};
